import React from 'react';
import * as StyledSystem from 'styled-system';
import * as H from 'history';
import type * as CSS from 'csstype'; // eslint-disable-line import/no-unresolved
import { Theme, zIndices, pounceShadows } from '../theme';
import { UtilityProps } from './utility';
import { PseudoProps } from './pseudo';

// prettier-ignore
// Gather all the styled-system props that we are going to pass
type ThemedStyleProps =
  StyledSystem.SpaceProps<Theme, React.CSSProperties['margin']> &
  StyledSystem.ColorProps<Theme> &
  StyledSystem.LayoutProps<Theme> &
  StyledSystem.BackgroundProps<Theme> &
  StyledSystem.GridProps<Theme> &
  StyledSystem.ShadowProps<Theme> &
  StyledSystem.BorderProps<Theme> &
  StyledSystem.FlexboxProps<Theme> &
  StyledSystem.TypographyProps<Theme>& 
  Omit<StyledSystem.PositionProps<Theme>, "zIndex">;

// Props related to Link components in @reach/router & react-router
type RoutingProps = {
  /** The url path to navigate, if the component implements a Link
   * @ignore
   */
  to?:
    | H.LocationDescriptor<H.LocationState>
    | ((location: H.Location<H.LocationState>) => H.LocationDescriptor<H.LocationState>);
};

// Props related to the usage of the Emotion CSS-in-JS library
type EmotionProps = {
  /** The React Component or native HTML element to render instead.
   * @default "div"
   * @ignore
   */
  as?: React.ElementType;
};

// Props related to data attributes (data-*)
type DataAttributeProps = {
  /** An HTML attribute that overrides the default RUM & Session Replay privacy option.
   * @default "mask"
   * @link https://docs.datadoghq.com/real_user_monitoring/session_replay/privacy_options/#privacy-options
   * @ignore
   */
  'data-dd-privacy'?: 'allow' | 'mask' | 'hidden' | 'mask-user-input';
};

// Gather the custom-named props that styled-system should accept
export interface CustomStyleProps {
  boxShadow?: StyledSystem.ResponsiveValue<pounceShadows | CSS.Property.BoxShadow>;
  shadow?: StyledSystem.ResponsiveValue<pounceShadows | CSS.Property.BoxShadow>;
  fill?: ThemedStyleProps['color'];
  stroke?: ThemedStyleProps['color'];
  textDecoration?: StyledSystem.ResponsiveValue<React.CSSProperties['textDecoration']>;
  textTransform?: StyledSystem.ResponsiveValue<React.CSSProperties['textTransform']>;
  animation?: StyledSystem.ResponsiveValue<React.CSSProperties['animation']>;
  transform?: StyledSystem.ResponsiveValue<React.CSSProperties['transform']>;
  visibility?: StyledSystem.ResponsiveValue<React.CSSProperties['visibility']>;
  whiteSpace?: StyledSystem.ResponsiveValue<React.CSSProperties['whiteSpace']>;
  pointerEvents?: StyledSystem.ResponsiveValue<React.CSSProperties['pointerEvents']>;
  wordWrap?: StyledSystem.ResponsiveValue<React.CSSProperties['wordWrap']>;
  wordBreak?: StyledSystem.ResponsiveValue<React.CSSProperties['wordBreak']>;
  overflowWrap?: StyledSystem.ResponsiveValue<React.CSSProperties['overflowWrap']>;
  textOverflow?: StyledSystem.ResponsiveValue<React.CSSProperties['textOverflow']>;
  cursor?: StyledSystem.ResponsiveValue<React.CSSProperties['cursor']>;
  resize?: StyledSystem.ResponsiveValue<React.CSSProperties['resize']>;
  transition?: StyledSystem.ResponsiveValue<React.CSSProperties['transition']>;
  objectFit?: StyledSystem.ResponsiveValue<React.CSSProperties['objectFit']>;
  outline?: StyledSystem.ResponsiveValue<React.CSSProperties['outline']>;
  userSelect?: StyledSystem.ResponsiveValue<React.CSSProperties['userSelect']>;
  transformOrigin?: StyledSystem.ResponsiveValue<React.CSSProperties['transformOrigin']>;
  willChange?: StyledSystem.ResponsiveValue<React.CSSProperties['willChange']>;
  borderCollapse?: StyledSystem.ResponsiveValue<React.CSSProperties['borderCollapse']>;
  borderSpacing?: StyledSystem.ResponsiveValue<React.CSSProperties['borderSpacing']>;
  tableLayout?: StyledSystem.ResponsiveValue<React.CSSProperties['tableLayout']>;
  gap?: StyledSystem.ResponsiveValue<React.CSSProperties['gap']>;
  columnCount?: StyledSystem.ResponsiveValue<React.CSSProperties['columnCount']>;
  zIndex?: StyledSystem.ResponsiveValue<zIndices | CSS.Property.ZIndex>;
}

export const customStyleProps: Record<keyof CustomStyleProps, boolean | StyledSystem.ConfigStyle> =
  {
    zIndex: {
      property: 'zIndex' as const,
      scale: 'zIndices',
    },
    boxShadow: {
      property: 'boxShadow' as const,
      scale: 'pounceShadows',
    },
    shadow: {
      property: 'boxShadow' as const,
      scale: 'pounceShadows',
    },
    fill: {
      property: 'fill' as const,
      scale: 'colors',
    },
    stroke: {
      property: 'stroke' as const,
      scale: 'colors',
    },
    gap: {
      property: 'gap' as const,
      scale: 'space',
    },
    textDecoration: true,
    textTransform: true,
    animation: true,
    transform: true,
    visibility: true,
    whiteSpace: true,
    pointerEvents: true,
    wordBreak: true,
    wordWrap: true,
    overflowWrap: true,
    textOverflow: true,
    cursor: true,
    columnCount: true,
    resize: true,
    transition: true,
    objectFit: true,
    outline: true,
    willChange: true,
    userSelect: true,
    borderCollapse: true,
    borderSpacing: true,
    tableLayout: true,
    transformOrigin: true,
  };

export type StylingProps = ThemedStyleProps & CustomStyleProps;
export const stylingProps = StyledSystem.compose(
  StyledSystem.space,
  StyledSystem.color,
  StyledSystem.layout,
  StyledSystem.background,
  StyledSystem.grid,
  StyledSystem.shadow,
  StyledSystem.border,
  StyledSystem.position,
  StyledSystem.flexbox,
  StyledSystem.borderRadius,
  StyledSystem.typography,
  StyledSystem.zIndex,
  StyledSystem.system(customStyleProps),
);

// All of the allowed props gathered together
export type SystemProps = StylingProps &
  RoutingProps &
  EmotionProps &
  PseudoProps &
  UtilityProps &
  DataAttributeProps;

export type SystemPropsWithoutAs = Omit<SystemProps, 'as'>;
